import React, { useState } from 'react';
import { Button } from '@smarterservices/smarter-ui';
import { useExamSessionStore } from '../../../stores/StoreContext';
import SendToOnboardingStepModal from './SendToOnboardingStepModal';

/**
 * SendToOnboardingStepDropdown Component
 * 
 * Renders a button that opens a modal allowing proctors to send users back to specific 
 * onboarding steps during check-in. Supports selecting multiple steps and optionally 
 * unfocusing the session after sending the user back.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Function} [props.onSendbackStart] - Optional callback fired before sending user to steps
 * @param {Object} props.onSendbackStart.params - Callback parameters
 * @param {string[]} props.onSendbackStart.params.stepIds - Array of step IDs to send user back to
 * @param {boolean} props.onSendbackStart.params.unfocusSession - Whether to unfocus the session
 * 
 * @returns {JSX.Element} Button with modal for sending users back to onboarding steps
 * 
 * @example
 * // Basic usage
 * <SendToOnboardingStepDropdown />
 * 
 * @example
 * // With callback
 * <SendToOnboardingStepDropdown 
 *   onSendbackStart={({ stepIds, unfocusSession }) => {
 *     console.log('Sending back to steps:', stepIds);
 *   }}
 * />
 */
const SendToOnboardingStepDropdown = ({ onSendbackStart }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const ExamSessionStore = useExamSessionStore();

    const handleConfirm = ({ stepIds, unfocusSession }) => {
        if (onSendbackStart) {
            onSendbackStart({ stepIds, unfocusSession });
        }
        ExamSessionStore.sendToStep({ stepIds, unfocusSession });
        setModalOpen(false);
    };

    return (
        <>
            <Button color="primary" size="small" onClick={() => setModalOpen(true)}>
                Send to Step
            </Button>

            <SendToOnboardingStepModal
                open={modalOpen}
                onDismiss={() => setModalOpen(false)}
                onConfirm={handleConfirm}
            />
        </>
    );
};

export default SendToOnboardingStepDropdown;
